import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Row, Col, InputNumber } from 'antd';
import { PendingItem } from '../lib/api';

const { Option } = Select;
const { TextArea } = Input;

interface EditTagsModalProps {
  visible: boolean;
  item: PendingItem | null;
  onSave: (itemId: string, metaData: Partial<PendingItem['meta_data']>) => void;
  onCancel: () => void;
  loading?: boolean;
}

export const EditTagsModal: React.FC<EditTagsModalProps> = ({
  visible,
  item,
  onSave,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && item) {
      const metaData = item.meta_data || {};
      form.setFieldsValue({
        name: metaData.name || '',
        category: metaData.category_level_2 || metaData.category || '',
        category_level_1: metaData.category_level_1 || '',
        category_level_2: metaData.category_level_2 || metaData.category || '',
        color: metaData.main_color || metaData.color || '',
        main_color: metaData.main_color || metaData.color || '',
        style: metaData.style_keyword || metaData.style || '',
        style_keyword: metaData.style_keyword || metaData.style || '',
        size: metaData.size || '',
        brand: metaData.brand || '',
        material: metaData.material_main || metaData.material || '',
        material_main: metaData.material_main || metaData.material || '',
        season: metaData.season || '',
        pattern: metaData.pattern || '',
        occasion: metaData.occasion || '',
        body_zone: metaData.body_zone || '',
        fit_type: metaData.fit_type || '',
        formality_score: metaData.formality_score || 2,
        length_detail: metaData.length_detail || '',
        closure_type: metaData.closure_type || '',
        notes: metaData.notes || '',
      });
    }
  }, [visible, item, form]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      if (item) {
        const metaData: Partial<PendingItem['meta_data']> = {
          name: values.name,
          category: values.category_level_2 || values.category,
          category_level_1: values.category_level_1,
          category_level_2: values.category_level_2 || values.category,
          color: values.main_color || values.color,
          main_color: values.main_color || values.color,
          style: values.style_keyword || values.style,
          style_keyword: values.style_keyword || values.style,
          size: values.size,
          brand: values.brand,
          material: values.material_main || values.material,
          material_main: values.material_main || values.material,
          season: values.season,
          pattern: values.pattern,
          occasion: values.occasion,
          body_zone: values.body_zone,
          fit_type: values.fit_type,
          formality_score: values.formality_score,
          length_detail: values.length_detail,
          closure_type: values.closure_type,
          notes: values.notes,
        };
        onSave(item.id, metaData);
      }
    });
  };

  return (
    <Modal
      title="Edit Item Tags"
      open={visible}
      onOk={handleSave}
      onCancel={onCancel}
      confirmLoading={loading}
      width={800}
      okText="Save"
      cancelText="Cancel"
    >
      <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="name" label="Name">
              <Input placeholder="Item name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="category_level_1" label="Category Level 1">
              <Input placeholder="e.g., Tops, Bottoms" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="category_level_2" label="Category Level 2">
              <Input placeholder="e.g., T-Shirt, Jeans" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="main_color" label="Main Color">
              <Input placeholder="e.g., Black, Blue" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="size" label="Size">
              <Input placeholder="e.g., S, M, L" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="brand" label="Brand">
              <Input placeholder="Brand name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="material_main" label="Material">
              <Input placeholder="e.g., Cotton, Polyester" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="style_keyword" label="Style">
              <Input placeholder="e.g., Casual, Formal" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="season" label="Season">
              <Select placeholder="Select season">
                <Option value="spring">Spring</Option>
                <Option value="summer">Summer</Option>
                <Option value="fall">Fall</Option>
                <Option value="winter">Winter</Option>
                <Option value="all-season">All Season</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="pattern" label="Pattern">
              <Input placeholder="e.g., Solid, Striped, Floral" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="occasion" label="Occasion">
              <Input placeholder="e.g., Casual, Formal, Party" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="body_zone" label="Body Zone">
              <Select placeholder="Select body zone">
                <Option value="upper-body">Upper Body</Option>
                <Option value="lower-body">Lower Body</Option>
                <Option value="full-body">Full Body</Option>
                <Option value="accessories">Accessories</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="fit_type" label="Fit Type">
              <Select placeholder="Select fit type">
                <Option value="slim">Slim</Option>
                <Option value="regular">Regular</Option>
                <Option value="loose">Loose</Option>
                <Option value="oversized">Oversized</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="formality_score" label="Formality Score (1-5)">
              <InputNumber min={1} max={5} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="closure_type" label="Closure Type">
              <Input placeholder="e.g., Zipper, Buttons" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="length_detail" label="Length Detail">
              <Input placeholder="e.g., Short, Medium, Long" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="notes" label="Notes">
              <TextArea rows={3} placeholder="Additional notes..." />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

